import React from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import TextController from "../form-fields/TextController";
import SwitchController from "../form-fields/SwitchController";

const roleSchema = z.object({
  role: z
    .string()
    .trim()
    .min(1, "Role name is required.")
    .min(2, "Role name must be at least 2 characters."),
  can_manage_users: z.boolean(),
  can_manage_clients: z.boolean(),
  can_view_reports: z.boolean(),
  can_edit_preferences: z.boolean(),
});

type RoleValues = z.infer<typeof roleSchema>;

interface EditRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: RoleValues) => void;
  initialData: RoleValues;
}

const EditRoleModal: React.FC<EditRoleModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData,
}) => {
  const form = useForm<RoleValues>({
    resolver: zodResolver(roleSchema),
    defaultValues: initialData,
  });

  const onSubmit = (data: RoleValues) => {
    onSave(data);
    form.reset();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Role</DialogTitle>
          <DialogDescription>Modify the role's details.</DialogDescription>
        </DialogHeader>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <TextController
              name="role"
              label="Role Name"
              placeholder="Enter role name"
              defaultValue={initialData.role}
            />
            <SwitchController
              name="can_manage_users"
              label="Can Manage Users"
              infoMessage=""
              defaultValue={false}
            />
            <SwitchController
              name="can_manage_clients"
              label="Can Manage Clients"
              infoMessage=""
              defaultValue={false}
            />
            <SwitchController
              name="can_view_reports"
              label="Can View Reports"
              infoMessage=""
              defaultValue={false}
            />
            <SwitchController
              name="can_edit_preferences"
              label="Can Edit Preferences"
              infoMessage=""
              defaultValue={false}
            />
            <DialogFooter>
              <Button type="submit">Save Changes</Button>
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};

export default EditRoleModal;
